import { useContext, useEffect, useLayoutEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Route, Routes, useLocation } from 'react-router-dom';
import { AppStyled } from './App.styles';
import './bootstrap-styles.scss';
import {
  BrandsProvider,
  CandidateModelsProvider,
  StepperProvider,
} from './contexts';
import { FlowStep, GlobalContext } from './contexts/GlobalContext';
import { useMetrics, useThirdPartyAnalytics } from './hooks';
import i18n from './i18n';
import {
  CompatibilityChecker,
  InfoNeeded,
  NotCompatible,
  NotCompatibleAmazon,
  Success,
} from './pages';
import './style-amz.css';
import './style.css';
import { AppOutcome } from './types';
import {
  getBrowserName,
  getUserPlatform,
  isAmazonHost,
  metricEvents,
  sessionStorageUtils,
} from './utils';
import { getProductIdFromUrl } from './utils/getProductIdFromUrl';

const App = () => {
  useThirdPartyAnalytics();
  const { state: globalState } = useContext(GlobalContext);
  const { track } = useMetrics();
  const location = useLocation();

  if (!isAmazonHost() && location.pathname !== `/${AppOutcome.Compatible}`) {
    sessionStorageUtils.clearContextsSessionStorage();
  }

  useEffect(() => {
    track(metricEvents.appLoad, {
      userPlatform: getUserPlatform(),
      isAmazonHost: isAmazonHost(),
      browserName: getBrowserName(),
      productId: getProductIdFromUrl(),
    });
  }, []);

  useLayoutEffect(() => {
    const appHeight = () => {
      const doc = document.documentElement;
      doc.style.setProperty('--app-height', `${window.innerHeight}px`);
    };
    appHeight();

    window.addEventListener('resize', appHeight);
    return () => {
      window.removeEventListener('resize', appHeight);
    };
  }, []);

  const renderView = () => {
    switch (globalState.flowStep) {
      case FlowStep.compatibilityChecker:
        return <CompatibilityChecker />;
      case FlowStep.notCompatible:
        return isAmazonHost() ? <NotCompatibleAmazon /> : <NotCompatible />;
      case FlowStep.success:
        return <Success />;
      case FlowStep.infoNeeded:
        return <InfoNeeded />;
    }
  };

  return (
    <I18nextProvider i18n={i18n}>
      <AppStyled className="container-fluid h-100" isAmazon={isAmazonHost()}>
        <StepperProvider>
          <BrandsProvider>
            <CandidateModelsProvider>
              {isAmazonHost() ? (
                renderView()
              ) : (
                <Routes>
                  <Route path="/" element={<CompatibilityChecker />} />
                  <Route
                    path={AppOutcome.NotCompatible}
                    element={<NotCompatible />}
                  />
                  <Route path={AppOutcome.Compatible} element={<Success />} />
                  <Route
                    path={AppOutcome.InfoNeeded}
                    element={<InfoNeeded />}
                  />
                </Routes>
              )}
            </CandidateModelsProvider>
          </BrandsProvider>
        </StepperProvider>
      </AppStyled>
    </I18nextProvider>
  );
};

export default App;
